import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Plus,
  Filter,
  Eye,
  Edit,
  MapPin,
  Camera,
  Users
} from 'lucide-react';
import { galleryService } from '@/services/galleryService';
import { Button } from '@/components/ui/Button/Button';
import {
  Gallery,
  GalleryFilters as GalleryFiltersType,
  GalleryListParams,
  GalleryStatus
} from '@/types/gallery';
import { useAuth } from '@/hooks/useAuth';
import { GalleryFilters } from './GalleryFilters';
import { PROGRAM_OPTIONS } from '../../constants';

interface GalleryListProps {
  onCreateClick: () => void;
  onGalleryClick: (galleryId: number) => void;
}


const STATUS_OPTIONS: Array<{ id: GalleryStatus; name: string; color: string }> = [
  { id: 'Active', name: 'Active', color: 'bg-green-100 text-green-800' },
  { id: 'Inactive', name: 'Inactive', color: 'bg-gray-100 text-gray-800' },
];

export const GalleryList: React.FC<GalleryListProps> = ({
  onCreateClick,
  onGalleryClick
}) => {
  const { user } = useAuth();
  const [filters, setFilters] = useState<GalleryFiltersType>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Check user permissions
  const canModerate = user?.roles?.some(role => 
    ['GALLERY.MODERATOR', 'ADMIN'].includes(role)
  );

  // Build query parameters
  const queryParams: GalleryListParams = {
    page: currentPage,
    page_size: 10,
    sort_by: 'created_at',
    sort_order: 'desc',
    ...filters,
  };

  // Fetch galleries
  const {
    data: galleriesResponse,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['galleries', queryParams],
    queryFn: () => galleryService.getGalleries(queryParams),
    placeholderData: (previousData) => previousData,
  });


  const galleries = galleriesResponse?.data?.list || [];
  const pageInfo = galleriesResponse?.data?.pageInfo;

  const handleFilterChange = (newFilters: Partial<GalleryListParams>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };


  const renderGalleryCard = (gallery: Gallery) => (
    <div 
      key={gallery.id}
      className="bg-white rounded-lg border border-rgf-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-lg font-semibold text-rgf-gray-900 truncate">
                {gallery.title}
              </h3>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                STATUS_OPTIONS.find(s => s.id === gallery.status)?.color
              }`}>
                {gallery.status}
              </span>
            </div>
            
            {gallery.description && (
              <p className="text-sm text-rgf-gray-600 mb-3 line-clamp-2">
                {gallery.description}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-4 text-sm text-rgf-gray-500">
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                <span>{gallery.village_name}, {gallery.district_name}</span>
              </div>
              
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-1" />
                <span>{PROGRAM_OPTIONS.find(p => p.value === gallery.program_id)?.label || gallery.program_id}</span>
              </div>
              
              <div className="flex items-center">
                <Camera className="h-4 w-4 mr-1" />
                <span>{gallery.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 ml-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onGalleryClick(gallery.id)}
              icon={<Eye className="h-4 w-4" />}
            >
              View
            </Button>
            
            {canModerate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onGalleryClick(gallery.id)}
                icon={<Edit className="h-4 w-4" />}
              >
                Edit
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-800">Error loading galleries: {error.message}</p>
        <Button 
          variant="secondary" 
          className="mt-4"
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <Button
            variant="secondary"
            onClick={() => setShowFilters(!showFilters)}
            icon={<Filter className="h-4 w-4" />}
          >
            Filters
          </Button>
          
          {canModerate && (
            <Button
              onClick={onCreateClick}
              icon={<Plus className="h-4 w-4" />}
            >
              Create Gallery
            </Button>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <GalleryFilters
          filters={filters}
          onFiltersChange={handleFilterChange}
        />
      )}

      {/* Gallery Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-rgf-gray-200 p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-rgf-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-rgf-gray-200 rounded mb-4 w-3/4"></div>
                <div className="space-y-2">
                  <div className="h-3 bg-rgf-gray-200 rounded w-1/2"></div>
                  <div className="h-3 bg-rgf-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : galleries.length === 0 ? (
        <div className="text-center py-12">
          <Camera className="mx-auto h-12 w-12 text-rgf-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-rgf-gray-900">No galleries found</h3>
          <p className="mt-1 text-sm text-rgf-gray-500">
            {Object.keys(filters).length > 0 ?
              'Try adjusting your filter criteria.' :
              'Get started by creating a new gallery.'
            }
          </p>
          {canModerate && !Object.keys(filters).length && (
            <div className="mt-6">
              <Button onClick={onCreateClick} icon={<Plus className="h-4 w-4" />}>
                Create Gallery
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {galleries.map(renderGalleryCard)}
        </div>
      )}

      {/* Pagination */}
      {pageInfo && pageInfo.totalRows > pageInfo.pageSize && (
        <div className="flex items-center justify-between">
          <div className="flex items-center text-sm text-rgf-gray-700">
            Showing {(pageInfo.page - 1) * pageInfo.pageSize + 1} to{' '}
            {Math.min(pageInfo.page * pageInfo.pageSize, pageInfo.totalRows)} of{' '}
            {pageInfo.totalRows} results
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={pageInfo.isFirstPage}
            >
              Previous
            </Button>
            
            <span className="text-sm text-rgf-gray-700">
              Page {pageInfo.page}
            </span>
            
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={pageInfo.isLastPage}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};